import { httpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import type {
  StudyBudget,
  SiteContract,
  Payment,
  FinancialDashboardData,
  DashboardSummary,
  PaymentDistribution,
  SitePaymentItem,
  FinanceAuditLog,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  CreateBudgetCategoryRequest,
  UpdateBudgetCategoryRequest,
  CreateContractRequest,
  UpdateContractRequest,
  CreateContractVersionRequest,
  SubmitInvoiceRequest,
  UpdateInvoiceRequest,
  ConfirmPaymentRequest,
  FinanceFilters,
  AuditLogFilters,
  FinanceListResponse,
} from "@/pages/Finance/types";
import type {
  BudgetExpenditure,
  CategoryTypeOption,
  CreateExpenditureRequest,
  DocumentDownloadUrlResponse,
  TriggerTypeOption,
  UploadContractDocumentResponse,
  UploadInvoiceDocumentResponse,
  UploadPaymentProofResponse,
} from "@/types/finance.type";

/**
 * Assert API response is successful and has data. Throws on failure.
 */
function assertSuccess<T>(
  response: ApiResponse<T>,
  errorMsg: string,
): asserts response is ApiResponse<T> & { data: T } {
  const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
  if (!isSuccess) {
    throw new Error(response.error ?? response.message ?? errorMsg);
  }
  if (!response.data) {
    throw new Error(`${errorMsg}: No data returned from server.`);
  }
}

/**
 * Assert API response is successful (no data required).
 */
function assertSuccessNoData(
  response: ApiResponse<unknown>,
  errorMsg: string,
): void {
  const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
  if (!isSuccess) {
    throw new Error(response.error ?? response.message ?? errorMsg);
  }
}

// ===================== Repository Interface =====================

export interface IFinanceRepository {
  // Budget methods
  createBudget(budget: CreateBudgetRequest): Promise<StudyBudget>;
  getBudgets(
    filters?: FinanceFilters,
    page?: number,
    limit?: number,
  ): Promise<FinanceListResponse<StudyBudget>>;
  getBudgetByStudy(studyId: string): Promise<StudyBudget>;
  getBudgetById(id: string): Promise<StudyBudget>;
  updateBudget(id: string, updates: UpdateBudgetRequest): Promise<StudyBudget>;
  addBudgetCategory(
    budgetId: string,
    category: CreateBudgetCategoryRequest,
  ): Promise<StudyBudget>;
  updateBudgetCategory(
    budgetId: string,
    categoryId: string,
    updates: UpdateBudgetCategoryRequest,
  ): Promise<StudyBudget>;
  deleteBudgetCategory(
    budgetId: string,
    categoryId: string,
  ): Promise<StudyBudget>;
  deleteBudget(id: string): Promise<void>;
  getCategoryTypes(): Promise<CategoryTypeOption[]>;

  // Expenditure methods
  recordExpenditure(
    budgetId: string,
    categoryId: string,
    data: CreateExpenditureRequest,
  ): Promise<BudgetExpenditure>;
  getExpenditures(
    budgetId: string,
    categoryId: string,
  ): Promise<{
    items: BudgetExpenditure[];
    total: number;
    totalSpent: number;
  }>;
  deleteExpenditure(
    budgetId: string,
    categoryId: string,
    expenditureId: string,
  ): Promise<void>;

  // Contract methods
  createContract(contract: CreateContractRequest): Promise<SiteContract>;
  getContracts(
    filters?: FinanceFilters,
    page?: number,
    limit?: number,
  ): Promise<FinanceListResponse<SiteContract>>;
  getContractByStudySite(
    studyId: string,
    siteId: string,
  ): Promise<SiteContract>;
  getContractById(id: string): Promise<SiteContract>;
  updateContract(
    id: string,
    updates: UpdateContractRequest,
  ): Promise<SiteContract>;
  createContractVersion(
    id: string,
    data?: CreateContractVersionRequest,
  ): Promise<SiteContract>;
  getContractVersions(id: string): Promise<SiteContract[]>;
  uploadContractDocument(file: File): Promise<UploadContractDocumentResponse>;
  getContractDownloadUrl(id: string): Promise<DocumentDownloadUrlResponse>;
  deleteContract(id: string): Promise<void>;
  /** Get available payment trigger types */
  getTriggerTypes(): Promise<TriggerTypeOption[]>;

  // Payment methods
  getPayments(
    filters?: FinanceFilters,
    page?: number,
    limit?: number,
  ): Promise<FinanceListResponse<Payment>>;
  getPaymentsByContract(contractId: string): Promise<Payment[]>;
  getPaymentById(id: string): Promise<Payment>;
  markPaymentDue(id: string): Promise<Payment>;
  submitInvoice(id: string, invoice: SubmitInvoiceRequest): Promise<Payment>;
  updateInvoice(id: string, invoice: UpdateInvoiceRequest): Promise<Payment>;
  confirmPayment(id: string, data: ConfirmPaymentRequest): Promise<Payment>;
  lockPayment(id: string): Promise<Payment>;
  deletePayment(id: string): Promise<void>;
  uploadInvoiceDocument(
    id: string,
    file: File,
  ): Promise<UploadInvoiceDocumentResponse>;
  getInvoiceDownloadUrl(id: string): Promise<DocumentDownloadUrlResponse>;
  uploadPaymentProofDocument(
    id: string,
    file: File,
  ): Promise<UploadPaymentProofResponse>;
  getPaymentProofDownloadUrl(id: string): Promise<DocumentDownloadUrlResponse>;

  // Dashboard methods
  getFinancialDashboard(studyId: string): Promise<FinancialDashboardData>;

  // Audit log methods
  getFinanceAuditLogs(
    filters?: AuditLogFilters,
    page?: number,
    limit?: number,
  ): Promise<FinanceListResponse<FinanceAuditLog>>;
  exportFinanceAuditLogs(studyId: string, format?: string): Promise<Blob>;
}

// ===================== Repository Implementation =====================

export class FinanceRepository implements IFinanceRepository {
  // ---------------------- Budget ----------------------

  async createBudget(budget: CreateBudgetRequest): Promise<StudyBudget> {
    const response = await httpCoreService.post<
      StudyBudget,
      CreateBudgetRequest
    >("/finance/budgets", budget);
    assertSuccess(response, "Failed to create budget");
    return response.data;
  }

  async getBudgets(
    filters?: FinanceFilters,
    page = 1,
    limit = 10,
  ): Promise<FinanceListResponse<StudyBudget>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters?.studyId) {
      params.append("studyId", filters.studyId);
    }
    if (filters?.includeInactive) {
      params.append("includeInactive", String(filters.includeInactive));
    }

    const response = await httpCoreService.get<
      FinanceListResponse<StudyBudget>
    >(`/finance/budgets?${params.toString()}`);
    assertSuccess(response, "Failed to fetch budgets");
    return response.data;
  }

  async getBudgetByStudy(studyId: string): Promise<StudyBudget> {
    const response = await httpCoreService.get<StudyBudget>(
      `/finance/budgets/by-study/${studyId}`,
    );
    assertSuccess(response, "Failed to fetch budget for study");
    return response.data;
  }

  async getBudgetById(id: string): Promise<StudyBudget> {
    const response = await httpCoreService.get<StudyBudget>(
      `/finance/budgets/${id}`,
    );
    assertSuccess(response, "Failed to fetch budget");
    return response.data;
  }

  async updateBudget(
    id: string,
    updates: UpdateBudgetRequest,
  ): Promise<StudyBudget> {
    const response = await httpCoreService.patch<
      StudyBudget,
      UpdateBudgetRequest
    >(`/finance/budgets/${id}`, updates);
    assertSuccess(response, "Failed to update budget");
    return response.data;
  }

  async addBudgetCategory(
    budgetId: string,
    category: CreateBudgetCategoryRequest,
  ): Promise<StudyBudget> {
    const response = await httpCoreService.post<
      StudyBudget,
      CreateBudgetCategoryRequest
    >(`/finance/budgets/${budgetId}/categories`, category);
    assertSuccess(response, "Failed to add budget category");
    return response.data;
  }

  async updateBudgetCategory(
    budgetId: string,
    categoryId: string,
    updates: UpdateBudgetCategoryRequest,
  ): Promise<StudyBudget> {
    const response = await httpCoreService.patch<
      StudyBudget,
      UpdateBudgetCategoryRequest
    >(`/finance/budgets/${budgetId}/categories/${categoryId}`, updates);
    assertSuccess(response, "Failed to update budget category");
    return response.data;
  }

  async deleteBudgetCategory(
    budgetId: string,
    categoryId: string,
  ): Promise<StudyBudget> {
    const response = await httpCoreService.delete<StudyBudget>(
      `/finance/budgets/${budgetId}/categories/${categoryId}`,
    );
    assertSuccess(response, "Failed to delete budget category");
    return response.data;
  }

  async deleteBudget(id: string): Promise<void> {
    const response = await httpCoreService.delete<null>(
      `/finance/budgets/${id}`,
    );
    assertSuccessNoData(response, "Failed to delete budget");
  }

  async getCategoryTypes(): Promise<CategoryTypeOption[]> {
    const response = await httpCoreService.get<{ types: CategoryTypeOption[] }>(
      "/finance/budgets/category-types",
    );
    assertSuccess(response, "Failed to fetch category types");
    return response.data?.types ?? [];
  }

  // ---------------------- Expenditures ----------------------

  async recordExpenditure(
    budgetId: string,
    categoryId: string,
    data: CreateExpenditureRequest,
  ): Promise<BudgetExpenditure> {
    const response = await httpCoreService.post<
      BudgetExpenditure,
      CreateExpenditureRequest
    >(
      `/finance/budgets/${budgetId}/categories/${categoryId}/expenditures`,
      data,
    );
    assertSuccess(response, "Failed to record expenditure");
    return response.data;
  }

  async getExpenditures(
    budgetId: string,
    categoryId: string,
  ): Promise<{
    items: BudgetExpenditure[];
    total: number;
    totalSpent: number;
  }> {
    const response = await httpCoreService.get<{
      items: BudgetExpenditure[];
      total: number;
      totalSpent: number;
    }>(`/finance/budgets/${budgetId}/categories/${categoryId}/expenditures`);
    assertSuccess(response, "Failed to fetch expenditures");
    return response.data;
  }

  async deleteExpenditure(
    budgetId: string,
    categoryId: string,
    expenditureId: string,
  ): Promise<void> {
    const response = await httpCoreService.delete<null>(
      `/finance/budgets/${budgetId}/categories/${categoryId}/expenditures/${expenditureId}`,
    );
    assertSuccessNoData(response, "Failed to delete expenditure");
  }

  async getTriggerTypes(): Promise<TriggerTypeOption[]> {
    const response = await httpCoreService.get<{
      types: TriggerTypeOption[];
    }>("/finance/contracts/trigger-types");
    assertSuccess(response, "Failed to fetch trigger types");
    return response.data?.types ?? [];
  }

  // ---------------------- Contract ----------------------

  async createContract(contract: CreateContractRequest): Promise<SiteContract> {
    const response = await httpCoreService.post<
      SiteContract,
      CreateContractRequest
    >("/finance/contracts", contract);
    assertSuccess(response, "Failed to create contract");
    return response.data;
  }

  async getContracts(
    filters?: FinanceFilters,
    page = 1,
    limit = 10,
  ): Promise<FinanceListResponse<SiteContract>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters?.studyId) {
      params.append("studyId", filters.studyId);
    }
    if (filters?.siteId) {
      params.append("siteId", filters.siteId);
    }
    if (filters?.triggerType) {
      params.append("triggerType", filters.triggerType);
    }
    if (filters?.includeInactive) {
      params.append("includeInactive", String(filters.includeInactive));
    }

    const response = await httpCoreService.get<
      FinanceListResponse<SiteContract>
    >(`/finance/contracts?${params.toString()}`);
    assertSuccess(response, "Failed to fetch contracts");
    return response.data;
  }

  async getContractByStudySite(
    studyId: string,
    siteId: string,
  ): Promise<SiteContract> {
    const params = new URLSearchParams({
      studyId,
      siteId,
    });
    const response = await httpCoreService.get<SiteContract>(
      `/finance/contracts/by-study-site?${params.toString()}`,
    );
    assertSuccess(response, "Failed to fetch contract for study/site");
    return response.data;
  }

  async getContractById(id: string): Promise<SiteContract> {
    const response = await httpCoreService.get<SiteContract>(
      `/finance/contracts/${id}`,
    );
    assertSuccess(response, "Failed to fetch contract");
    return response.data;
  }

  async updateContract(
    id: string,
    updates: UpdateContractRequest,
  ): Promise<SiteContract> {
    const response = await httpCoreService.patch<
      SiteContract,
      UpdateContractRequest
    >(`/finance/contracts/${id}`, updates);
    assertSuccess(response, "Failed to update contract");
    return response.data;
  }

  async createContractVersion(
    id: string,
    data?: CreateContractVersionRequest,
  ): Promise<SiteContract> {
    const response = await httpCoreService.post<
      SiteContract,
      CreateContractVersionRequest
    >(`/finance/contracts/${id}/new-version`, data ?? {});
    assertSuccess(response, "Failed to create contract version");
    return response.data;
  }

  async getContractVersions(id: string): Promise<SiteContract[]> {
    const response = await httpCoreService.get<SiteContract[]>(
      `/finance/contracts/${id}/versions`,
    );
    assertSuccess(response, "Failed to fetch contract versions");
    return response.data;
  }

  async uploadContractDocument(
    file: File,
  ): Promise<UploadContractDocumentResponse> {
    const formData = new FormData();
    formData.append("file", file);
    const response =
      await httpCoreService.postForm<UploadContractDocumentResponse>(
        `/finance/contracts/upload-document`,
        formData,
      );
    assertSuccess(response, "Failed to upload contract document");
    return response.data;
  }

  async getContractDownloadUrl(
    id: string,
  ): Promise<DocumentDownloadUrlResponse> {
    const response = await httpCoreService.get<DocumentDownloadUrlResponse>(
      `/finance/contracts/${id}/download-url`,
    );
    assertSuccess(response, "Failed to get contract download URL");
    return response.data;
  }

  async deleteContract(id: string): Promise<void> {
    const response = await httpCoreService.delete<null>(
      `/finance/contracts/${id}`,
    );
    assertSuccessNoData(response, "Failed to delete contract");
  }

  // ---------------------- Payment ----------------------

  async getPayments(
    filters?: FinanceFilters,
    page = 1,
    limit = 10,
  ): Promise<FinanceListResponse<Payment>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters?.studyId) {
      params.append("studyId", filters.studyId);
    }
    if (filters?.siteId) {
      params.append("siteId", filters.siteId);
    }
    if (filters?.status) {
      params.append("paymentStatus", filters.status);
    }
    if (filters?.includeInactive) {
      params.append("includeInactive", String(filters.includeInactive));
    }

    const response = await httpCoreService.get<FinanceListResponse<Payment>>(
      `/finance/payments?${params.toString()}`,
    );
    assertSuccess(response, "Failed to fetch payments");
    return response.data;
  }

  async getPaymentsByContract(contractId: string): Promise<Payment[]> {
    const params = new URLSearchParams({ contractId });
    const response = await httpCoreService.get<Payment[]>(
      `/finance/payments/by-contract?${params.toString()}`,
    );
    assertSuccess(response, "Failed to fetch payments for contract");
    return response.data;
  }

  async getPaymentById(id: string): Promise<Payment> {
    const response = await httpCoreService.get<Payment>(
      `/finance/payments/${id}`,
    );
    assertSuccess(response, "Failed to fetch payment");
    return response.data;
  }

  async markPaymentDue(id: string): Promise<Payment> {
    const response = await httpCoreService.post<Payment, unknown>(
      `/finance/payments/${id}/mark-due`,
      {},
    );
    assertSuccess(response, "Failed to mark payment as due");
    return response.data;
  }

  async submitInvoice(
    id: string,
    invoice: SubmitInvoiceRequest,
  ): Promise<Payment> {
    const response = await httpCoreService.post<Payment, SubmitInvoiceRequest>(
      `/finance/payments/${id}/submit-invoice`,
      invoice,
    );
    assertSuccess(response, "Failed to submit invoice");
    return response.data;
  }

  async updateInvoice(
    id: string,
    invoice: UpdateInvoiceRequest,
  ): Promise<Payment> {
    const response = await httpCoreService.patch<Payment, UpdateInvoiceRequest>(
      `/finance/payments/${id}/update-invoice`,
      invoice,
    );
    assertSuccess(response, "Failed to update invoice");
    return response.data;
  }

  async confirmPayment(
    id: string,
    data: ConfirmPaymentRequest,
  ): Promise<Payment> {
    const response = await httpCoreService.post<Payment, ConfirmPaymentRequest>(
      `/finance/payments/${id}/confirm-payment`,
      data,
    );
    assertSuccess(response, "Failed to confirm payment");
    return response.data;
  }

  async lockPayment(id: string): Promise<Payment> {
    const response = await httpCoreService.post<Payment, unknown>(
      `/finance/payments/${id}/lock`,
      {},
    );
    assertSuccess(response, "Failed to lock payment");
    return response.data;
  }

  async deletePayment(id: string): Promise<void> {
    const response = await httpCoreService.delete<null>(
      `/finance/payments/${id}`,
    );
    assertSuccessNoData(response, "Failed to delete payment");
  }

  async uploadInvoiceDocument(
    id: string,
    file: File,
  ): Promise<UploadInvoiceDocumentResponse> {
    const formData = new FormData();
    formData.append("file", file);
    const response =
      await httpCoreService.postForm<UploadInvoiceDocumentResponse>(
        `/finance/payments/${id}/upload-invoice-document`,
        formData,
      );
    assertSuccess(response, "Failed to upload invoice document");
    return response.data;
  }

  async getInvoiceDownloadUrl(
    id: string,
  ): Promise<DocumentDownloadUrlResponse> {
    const response = await httpCoreService.get<DocumentDownloadUrlResponse>(
      `/finance/payments/${id}/invoice/download-url`,
    );
    assertSuccess(response, "Failed to get invoice download URL");
    return response.data;
  }

  async uploadPaymentProofDocument(
    id: string,
    file: File,
  ): Promise<UploadPaymentProofResponse> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await httpCoreService.postForm<UploadPaymentProofResponse>(
      `/finance/payments/${id}/upload-payment-proof-document`,
      formData,
    );
    assertSuccess(response, "Failed to upload payment proof document");
    return response.data;
  }

  async getPaymentProofDownloadUrl(
    id: string,
  ): Promise<DocumentDownloadUrlResponse> {
    const response = await httpCoreService.get<DocumentDownloadUrlResponse>(
      `/finance/payments/${id}/payment-proof/download-url`,
    );
    assertSuccess(response, "Failed to get payment proof download URL");
    return response.data;
  }

  // ---------------------- Dashboard ----------------------

  async getFinancialDashboard(
    studyId: string,
  ): Promise<FinancialDashboardData> {
    const params = new URLSearchParams({ studyId });

    // Three parallel API calls compose the dashboard
    const [summaryRes, distributionRes, sitePaymentsRes] = await Promise.all([
      httpCoreService.get<DashboardSummary>(
        `/finance/dashboard/summary?${params.toString()}`,
      ),
      httpCoreService.get<PaymentDistribution>(
        `/finance/dashboard/payment-distribution?${params.toString()}`,
      ),
      httpCoreService.get<{ items: SitePaymentItem[] }>(
        `/finance/dashboard/site-payments?${params.toString()}`,
      ),
    ]);

    assertSuccess(summaryRes, "Failed to fetch dashboard summary");
    assertSuccess(distributionRes, "Failed to fetch payment distribution");
    assertSuccess(sitePaymentsRes, "Failed to fetch site payments");

    return {
      summary: summaryRes.data,
      paymentDistribution: distributionRes.data,
      sitePayments: sitePaymentsRes.data.items ?? sitePaymentsRes.data,
    };
  }

  // ---------------------- Audit Logs ----------------------

  async getFinanceAuditLogs(
    filters?: AuditLogFilters,
    page = 1,
    limit = 10,
  ): Promise<FinanceListResponse<FinanceAuditLog>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters?.studyId) {
      params.append("studyId", filters.studyId);
    }
    if (filters?.siteId) {
      params.append("siteId", filters.siteId);
    }
    if (filters?.action) {
      params.append("action", filters.action);
    }
    if (filters?.dateFrom) {
      params.append("dateFrom", filters.dateFrom);
    }
    if (filters?.dateTo) {
      params.append("dateTo", filters.dateTo);
    }
    if (filters?.userId) {
      params.append("userId", filters.userId);
    }
    if (filters?.search) {
      params.append("search", filters.search);
    }

    const response = await httpCoreService.get<
      FinanceListResponse<FinanceAuditLog>
    >(`/finance/audit-logs?${params.toString()}`);
    assertSuccess(response, "Failed to fetch audit logs");
    return response.data;
  }

  async exportFinanceAuditLogs(
    studyId: string,
    format?: string,
  ): Promise<Blob> {
    const params = new URLSearchParams({ studyId });
    if (format) {
      params.append("format", format);
    }
    return httpCoreService.getBlob(
      `/finance/audit-logs/export?${params.toString()}`,
    );
  }
}
